import React, { useState, useEffect } from 'react';
import {
  History, Calendar, Filter, Download, Edit2, Trash2,
  Check, X, ChevronDown, ChevronUp, Search, User,
  Clock, Award, AlertCircle, CalendarDays, ArrowUpDown,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { getTodayDateString, extractDateInTimezoneSync } from '../utils/timezoneUtils';
import toast from 'react-hot-toast';
import { getAssignedUserId } from '../utils/assignmentUtils';

interface HistoricalAssignment {
  id: string;
  chore_name: string;
  chore_id: string;
  assigned_to: string;
  assigned_to_name: string;
  assigned_by?: string;
  assigned_by_name?: string;
  status: string;
  points_earned: number;
  points_offered: number;
  assigned_at: string;
  due_date?: string;
  started_at?: string;
  completed_at?: string;
  verified_at?: string;
  verified_by?: string;
  verification_notes?: string;
  completion_notes?: string;
  category?: string;
}

interface FilterOptions {
  startDate: string;
  endDate: string;
  childFilter: string;
  statusFilter: string;
  categoryFilter: string;
  searchTerm: string;
}

export const AssignmentHistory: React.FC = () => {
  const [assignments, setAssignments] = useState<HistoricalAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [children, setChildren] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  // Edit form state
  const [editForm, setEditForm] = useState<{
    assignedTo: string;
    status: string;
    points: number;
    dueDate: string;
    notes: string;
  }>({
    assignedTo: '',
    status: '',
    points: 0,
    dueDate: '',
    notes: ''
  });
  
  // Filter state - use toLocaleDateString('en-CA') for YYYY-MM-DD in local timezone
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA'), // 90 days ago
    endDate: new Date().toLocaleDateString('en-CA'), // Today (local timezone)
    childFilter: 'all',
    statusFilter: 'all',
    categoryFilter: 'all',
    searchTerm: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'status' | 'child'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load assignments with filters - use the standard assignments endpoint with 'all' date filter
      const [assignmentsData, childrenData, categoriesData] = await Promise.all([
        apiClient.getAssignments(
          filters.statusFilter !== 'all' ? filters.statusFilter : undefined,
          filters.childFilter !== 'all' ? filters.childFilter : undefined,
          'all', // Use 'all' to get all assignments across all dates
          1000
        ),
        apiClient.getParentChildren(),
        apiClient.getChoreCategories().catch(() => []) // Handle error gracefully
      ]);
      
      // Filter by date range - both future dates and dates outside the selected range
      const startDate = parseLocalDate(filters.startDate);
      const endDate = parseLocalDate(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day

      const filteredAssignments = (assignmentsData || []).filter((a: any) => {
        // Use the most relevant date for filtering
        const relevantDate = a.completed_at || a.verified_at || a.due_date || a.assigned_at || a.created_at;
        if (!relevantDate) return true; // Include if no date is available
        const assignmentDate = parseLocalDate(relevantDate);
        return assignmentDate >= startDate && assignmentDate <= endDate;
      });
      
      // Map the data to expected format
      const mappedAssignments = filteredAssignments.map((a: any) => ({
        ...a,
        chore_name: a.chore?.name || 'Unknown Chore',
        category: a.chore?.category || 'uncategorized',
        assigned_to_name: a.assigned_user?.display_name || 'Unknown User',
        assigned_at: a.created_at,
        points_offered: a.points_offered || 0,
        points_earned: a.points_earned || 0
      }));
      
      // Filter by category and search term (date filtering is done by the backend now)
      let filtered = mappedAssignments.filter((a: any) => {
        if (filters.categoryFilter !== 'all' && a.category !== filters.categoryFilter) return false;
        
        if (filters.searchTerm) {
          const search = filters.searchTerm.toLowerCase();
          return (
            a.chore_name?.toLowerCase().includes(search) ||
            a.assigned_to_name?.toLowerCase().includes(search) ||
            a.completion_notes?.toLowerCase().includes(search)
          );
        }
        
        return true;
      });
      
      // Sort based on selected criteria
      filtered.sort((a: any, b: any) => {
        let compareValue = 0;
        
        switch (sortBy) {
          case 'date':
            const dateA = new Date(a.due_date || a.completed_at || a.assigned_at || a.created_at);
            const dateB = new Date(b.due_date || b.completed_at || b.assigned_at || b.created_at);
            // When desc: most recent first (Sep 4 before Aug 22)
            // When asc: oldest first (Aug 22 before Sep 4)  
            compareValue = dateB.getTime() - dateA.getTime();
            break;
          case 'status':
            compareValue = a.status.localeCompare(b.status);
            break;
          case 'child':
            compareValue = a.assigned_to_name.localeCompare(b.assigned_to_name);
            break;
        }
        
        return sortOrder === 'desc' ? compareValue : -compareValue;
      });

      setAssignments(filtered);
      setChildren(childrenData);
      
      // Extract unique categories from assignments if categories endpoint fails
      if (Array.isArray(categoriesData) && categoriesData.length > 0) {
        setCategories(categoriesData.map((c: any) => c.category));
      } else {
        // Fallback: extract categories from assignments
        const uniqueCategories = [...new Set((assignmentsData || [])
          .map((a: any) => a.category)
          .filter((c: string) => c)
        )].sort();
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load assignment history');
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleEdit = (assignment: HistoricalAssignment) => {
    // Check if we're already editing this row, if so, cancel the edit
    if (editingRow === assignment.id) {
      setEditingRow(null);
    } else {
      // First expand the row if it's not already expanded
      if (!expandedRows.has(assignment.id)) {
        const newExpanded = new Set(expandedRows);
        newExpanded.add(assignment.id);
        setExpandedRows(newExpanded);
      }
      // Initialize form state with current assignment values
      // Use getAssignedUserId utility for consistent ID format across data sources
      setEditForm({
        assignedTo: getAssignedUserId(assignment as any),
        status: assignment.status,
        points: assignment.points_offered || 0,
        dueDate: assignment.due_date ? extractDateInTimezoneSync(assignment.due_date) : '',
        notes: assignment.verification_notes || ''
      });
      setEditingRow(assignment.id);
    }
  };

  const handleSaveEdit = async (assignment: HistoricalAssignment, updates: any) => {
    try {
      // The backend PUT endpoint handles everything - status changes, point adjustments, etc.
      // It will automatically remove points if changing from verified status
      await apiClient.put(`/api/assignments/${assignment.id}`, updates);

      toast.success('Assignment updated successfully');
      setEditingRow(null);
      loadData();
    } catch (error: any) {
      console.error('Failed to update assignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to update assignment');
    }
  };

  const handleDelete = async (assignment: HistoricalAssignment) => {
    if (!confirm(`Delete assignment of "${assignment.chore_name}" to ${assignment.assigned_to_name}?`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/api/assignments/${assignment.id}`);
      toast.success('Assignment deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const handleVerify = async (assignment: HistoricalAssignment, approved: boolean) => {
    try {
      await apiClient.verifyAssignment(assignment.id, {
        approved,
        verification_notes: approved ? 'Approved via history' : 'Rejected via history'
      });
      toast.success(approved ? 'Assignment verified!' : 'Assignment rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to verify assignment');
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Child', 'Chore', 'Category', 'Status', 'Points Earned', 'Notes'];
    const rows = assignments.map(a => [
      new Date(a.due_date || a.completed_at || a.assigned_at).toLocaleDateString(),
      a.assigned_to_name,
      a.chore_name,
      a.category || 'N/A',
      a.status,
      a.points_earned.toString(),
      a.completion_notes || ''
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chore-history-${filters.startDate}-to-${filters.endDate}.csv`;
    link.click();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending_verification': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-gray-100 text-gray-800';
      case 'open': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const parseLocalDate = (dateString: string): Date => {
    // For YYYY-MM-DD format, parse as local date to avoid timezone issues
    if (dateString.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day, 12, 0, 0); // Set to noon to avoid DST issues
    }
    // For datetime strings, parse normally
    return new Date(dateString);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    
    const date = parseLocalDate(dateString);
    
    // If it's just a date (no time component), show date only
    if (dateString.length === 10) {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      });
    } else {
      // For datetime strings, show date and time
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const formatDateShort = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    
    const date = parseLocalDate(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    // For dates within the last week, show relative time
    if (diffInDays === 0) {
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
        return `${diffInMinutes} min ago`;
      }
      return `Today ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInDays === 1) {
      return `Yesterday`;
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    }
    
    // For older dates, show the actual date
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatDateLarge = (dateString: string | undefined) => {
    if (!dateString) return { day: '--', month: 'N/A', time: '', weekday: '--', year: 0 };
    
    const date = parseLocalDate(dateString);
    
    return {
      day: date.getDate().toString(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      year: date.getFullYear(),
      time: dateString.includes('T') ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '',
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' })
    };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading assignment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <History className="w-6 h-6 mr-2" />
            Assignment History
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
            </button>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
        
        {/* Sorting Controls */}
        <div className="flex items-center space-x-4 text-sm">
          <span className="text-gray-600">Sort by:</span>
          <button
            onClick={() => { setSortBy('date'); setSortOrder(sortBy === 'date' && sortOrder === 'desc' ? 'asc' : 'desc'); }}
            className={`px-3 py-1 rounded-lg flex items-center space-x-1 ${sortBy === 'date' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} transition-colors`}
            title={sortBy === 'date' ? `Sorted ${sortOrder === 'desc' ? 'newest first' : 'oldest first'}` : 'Sort by date'}
          >
            <CalendarDays className="w-4 h-4" />
            <span>Date</span>
            {sortBy === 'date' && (
              sortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
            )}
          </button>
          <button
            onClick={() => { setSortBy('status'); setSortOrder(sortBy === 'status' && sortOrder === 'desc' ? 'asc' : 'desc'); }}
            className={`px-3 py-1 rounded-lg flex items-center space-x-1 ${sortBy === 'status' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} transition-colors`}
            title={sortBy === 'status' ? `Sorted ${sortOrder === 'desc' ? 'completed first' : 'pending first'}` : 'Sort by status'}
          >
            <span>Status</span>
            {sortBy === 'status' && (
              sortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
            )}
          </button>
          <button
            onClick={() => { setSortBy('child'); setSortOrder(sortBy === 'child' && sortOrder === 'desc' ? 'asc' : 'desc'); }}
            className={`px-3 py-1 rounded-lg flex items-center space-x-1 ${sortBy === 'child' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} transition-colors`}
            title={sortBy === 'child' ? `Sorted ${sortOrder === 'desc' ? 'Z-A' : 'A-Z'}` : 'Sort by child'}
          >
            <User className="w-4 h-4" />
            <span>Child</span>
            {sortBy === 'child' && (
              sortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
            )}
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Child</label>
                <select
                  value={filters.childFilter}
                  onChange={(e) => {
                    setFilters({ ...filters, childFilter: e.target.value });
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Children</option>
                  {children.map(child => (
                    <option key={child.id} value={child.id}>
                      {child.display_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.statusFilter}
                  onChange={(e) => setFilters({ ...filters, statusFilter: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="pending_verification">Pending Verification</option>
                  <option value="in_progress">In Progress</option>
                  <option value="assigned">Assigned</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={filters.categoryFilter}
                  onChange={(e) => setFilters({ ...filters, categoryFilter: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search chores..."
                    value={filters.searchTerm}
                    onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Showing {assignments.length} assignments from {filters.startDate} to {filters.endDate}
              </p>
              <button
                onClick={() => setFilters({
                  startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA'),
                  endDate: new Date().toLocaleDateString('en-CA'),
                  childFilter: 'all',
                  statusFilter: 'all',
                  categoryFilter: 'all',
                  searchTerm: ''
                })}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="p-6">
        {assignments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No assignments found</p>
            <p className="text-sm mt-2">Try adjusting your filters or date range</p>
          </div>
        ) : (
          <div>
            {/* Table Headers */}
            <div className="hidden lg:grid grid-cols-6 gap-4 px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b bg-gray-50">
              <div>Chore</div>
              <div>Assigned To</div>
              <div>Status</div>
              <div>Points</div>
              <div>Date</div>
              <div className="text-right">Actions</div>
            </div>
            
            <div className="space-y-2">
            {assignments.map((assignment) => {
              const isExpanded = expandedRows.has(assignment.id);
              const isEditing = editingRow === assignment.id;
              
              const dateInfo = formatDateLarge(assignment.due_date || assignment.completed_at || assignment.assigned_at);
              
              return (
                <div key={assignment.id} className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow">
                  {/* Main Row with Date Card */}
                  <div className="flex items-stretch">
                    {/* Date Card - Very Prominent */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 flex flex-col items-center justify-center min-w-[100px] border-r">
                      <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wider mb-1">Due Date</div>
                      <div className="text-3xl font-bold text-blue-900">{dateInfo.day}</div>
                      <div className="text-sm font-medium text-blue-700">{dateInfo.month}</div>
                      <div className="text-xs text-blue-600">{dateInfo.weekday}</div>
                      {dateInfo.year !== new Date().getFullYear() && (
                        <div className="text-xs text-blue-600 mt-1">{dateInfo.year}</div>
                      )}
                    </div>
                    
                    {/* Assignment Details */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Top Row - Chore and Status */}
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-lg text-gray-900">{assignment.chore_name}</h3>
                              <p className="text-sm text-gray-500">{assignment.category || 'Uncategorized'}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(assignment.status)}`}>
                              {assignment.status.replace('_', ' ')}
                            </span>
                          </div>
                          
                          {/* Bottom Row - Child, Points, Time */}
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-1 text-gray-400" />
                              <span className="font-medium">{assignment.assigned_to_name}</span>
                            </div>
                            <div className="flex items-center">
                              <Award className="w-4 h-4 mr-1 text-yellow-500" />
                              <span className="font-medium">{assignment.points_earned || 0} / {assignment.points_offered} pts</span>
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1 text-gray-400" />
                              <span>{assignment.completed_at ? `Completed: ${formatDateShort(assignment.completed_at)}` : `Due: ${dateInfo.time}`}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => toggleRowExpansion(assignment.id)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          {(assignment.status === 'pending_verification' || 
                           (assignment.status === 'completed' && !assignment.verified_at)) && (
                            <>
                              <button
                                onClick={() => handleVerify(assignment, true)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                title="Approve"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleVerify(assignment, false)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Reject"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleEdit(assignment)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(assignment)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="bg-gray-50 p-4 border-t">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Assigned At</p>
                          <p className="font-medium">{formatDate(assignment.assigned_at)}</p>
                        </div>
                        {assignment.due_date && (
                          <div>
                            <p className="text-gray-600">Due Date</p>
                            <p className="font-medium">{formatDate(assignment.due_date)}</p>
                          </div>
                        )}
                        {assignment.started_at && (
                          <div>
                            <p className="text-gray-600">Started At</p>
                            <p className="font-medium">{formatDate(assignment.started_at)}</p>
                          </div>
                        )}
                        {assignment.completed_at && (
                          <div>
                            <p className="text-gray-600">Completed At</p>
                            <p className="font-medium">{formatDate(assignment.completed_at)}</p>
                          </div>
                        )}
                        {assignment.verified_at && (
                          <div>
                            <p className="text-gray-600">Verified At</p>
                            <p className="font-medium">{formatDate(assignment.verified_at)}</p>
                          </div>
                        )}
                        {assignment.assigned_by_name && (
                          <div>
                            <p className="text-gray-600">Assigned By</p>
                            <p className="font-medium">{assignment.assigned_by_name}</p>
                          </div>
                        )}
                      </div>
                      
                      {(assignment.completion_notes || assignment.verification_notes) && (
                        <div className="mt-4 space-y-2">
                          {assignment.completion_notes && (
                            <div>
                              <p className="text-sm text-gray-600">Completion Notes:</p>
                              <p className="text-sm bg-white p-2 rounded">{assignment.completion_notes}</p>
                            </div>
                          )}
                          {assignment.verification_notes && (
                            <div>
                              <p className="text-sm text-gray-600">Verification Notes:</p>
                              <p className="text-sm bg-white p-2 rounded">{assignment.verification_notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {isEditing && (
                        <div className="mt-4 p-4 bg-white rounded-lg border">
                          <h4 className="font-medium mb-3">Edit Assignment</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                              <select
                                value={editForm.assignedTo}
                                onChange={(e) => setEditForm({ ...editForm, assignedTo: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                              >
                                <option value="">Unassigned (Open)</option>
                                {(Array.isArray(children) ? children : []).map(child => (
                                  <option key={String(child.id)} value={String(child.id)}>
                                    {child.display_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                              <select
                                value={editForm.status}
                                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                              >
                                {!editForm.assignedTo ? (
                                  <option value="pending">Open (Available for bidding)</option>
                                ) : (
                                  <>
                                    <option value="pending">Pending (Not started)</option>
                                    <option value="in_progress">In progress (Working on it)</option>
                                    <option value="completed">Completed (Awaiting verification)</option>
                                    <option value="verified">Verified (Confirmed by parent)</option>
                                  </>
                                )}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Points Offered</label>
                              <input
                                type="number"
                                value={editForm.points}
                                onChange={(e) => setEditForm({ ...editForm, points: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                              <input
                                type="date"
                                value={editForm.dueDate}
                                onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                              <textarea
                                value={editForm.notes}
                                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                                rows={2}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end space-x-2 mt-4">
                            <button
                              onClick={() => setEditingRow(null)}
                              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                handleSaveEdit(assignment, {
                                  assigned_to: editForm.assignedTo || null,
                                  points_offered: editForm.points,
                                  status: editForm.status,
                                  due_date: editForm.dueDate || null,
                                  notes: editForm.notes || null
                                });
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              Save Changes
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};